import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

import { ACCENT_BLUE, EASE_EXPO, MUTED_GRAY, TEXT_LIGHT } from './constants';

export function GeoCard({ children, className = '', delay = 0, glow = ACCENT_BLUE }: { children: React.ReactNode; className?: string; delay?: number; glow?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }} transition={{ duration: 0.6, delay, ease: EASE_EXPO }} className={`group relative overflow-hidden ${className}`} style={{ clipPath: 'polygon(0 0, calc(100% - 24px) 0, 100% 24px, 100% 100%, 24px 100%, 0 calc(100% - 24px))' }}>
      <div className='relative h-full transition-all duration-500' style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.03), rgba(255,255,255,0.01))', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className='absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100' style={{ background: `radial-gradient(ellipse at top left, ${glow}10, transparent 60%)` }} />
        {/* Tailwind 3.4 has no `origin-start`, so the growth direction is flipped with an rtl: variant. */}
        <div className='absolute start-0 top-0 h-[2px] w-full origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100 rtl:origin-right' style={{ background: glow }} />
        <div className='relative z-10'>{children}</div>
        <div className='absolute bottom-0 end-0 size-8 border-b-2 border-e-2 opacity-0 transition-opacity duration-300 group-hover:opacity-40' style={{ borderColor: glow }} />
      </div>
    </motion.div>
  );
}

export function Section({ children, className = '', id, bg }: { children: React.ReactNode; className?: string; id?: string; bg?: React.ReactNode }) {
  return (
    <section id={id} className={`relative overflow-hidden px-4 py-20 md:py-32 ${className}`}>
      {bg}
      <div className='relative mx-auto max-w-7xl' style={{ zIndex: 1 }}>
        {children}
      </div>
    </section>
  );
}

export function SectionTitle({ subtitle, title, titleAccent, accentColor = ACCENT_BLUE }: { subtitle: string; title: string; titleAccent?: string; accentColor?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });

  return (
    <motion.div ref={ref} initial={{ opacity: 0, scale: 2.2, filter: 'blur(8px)' }} animate={inView ? { opacity: 1, scale: 1, filter: 'blur(0px)' } : { opacity: 0, scale: 2.2, filter: 'blur(8px)' }} transition={{ duration: 0.6, ease: EASE_EXPO }} className='mb-16 text-center'>
      <h2 className='text-4xl font-bold tracking-[-0.02em] md:text-6xl lg:text-7xl' style={{ fontFamily: 'var(--font-bai)', color: TEXT_LIGHT }}>
        {title} {titleAccent && <span style={{ color: accentColor }}>{titleAccent}</span>}
      </h2>
      <p className='mt-3 text-sm tracking-[0.15em] md:text-base' style={{ color: MUTED_GRAY, fontFamily: 'var(--font-outfit)' }}>
        {subtitle}
      </p>
    </motion.div>
  );
}

export function AccentDivider({ color = ACCENT_BLUE }: { color?: string }) {
  return <div className='absolute inset-x-0 top-0 h-[2px]' style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }} />;
}

export function Reveal({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-60px' });

  return (
    <motion.div ref={ref} initial={{ y: 60, opacity: 0 }} animate={inView ? { y: 0, opacity: 1 } : { y: 60, opacity: 0 }} transition={{ duration: 0.7, delay, ease: EASE_EXPO }} className={className}>
      {children}
    </motion.div>
  );
}
